import { IApiError } from '@frontend/request';

import { EMainProductCodeType } from '@aqx-core/common/api/config/model/types';
import { marketAPIController } from '@aqx-core/common/api/market';
import { tradeAPIController } from '@aqx-core/common/api/trade';
import { getSecondsSinceEpoch, getUTCOffsetDateTimeAsDayjs } from '@aqx-core/common/dayjs';
import { mapResponseCodeToMessage } from '@aqx-core/common/request';
import { companyService } from '@aqx-core/common/services/company/service';
import { orderListingViewController } from '@aqx-core/common/services/order/controller';
import { orderListingService } from '@aqx-core/common/services/order/service';
import { equityChartViewController } from '@aqx-core/common/services/trade/analysis/equity-chart/controller';
import { isMarketOrder } from '@aqx-core/common/utils/order';

import { ICloseOrderActionParams } from '../types';
import { openOrderService } from './service';

class OpenOrderViewController {
  public getDetailOrder = async (symbol: string, onError: (message: string) => void) => {
    openOrderService.setLoadingSymbolDetail(true);
    await marketAPIController.getSymbolDetail(
      { symbol },
      {
        onSuccess: (symbolDetail) => {
          if (symbolDetail) {
            openOrderService.setSymbolDetail(symbolDetail);
          }
        },
        onError: (error: IApiError) => {
          const { code, message } = error as IApiError;
          const errorMessage = mapResponseCodeToMessage(code, message);
          onError(errorMessage);
        },
        onFinally: () => {
          openOrderService.setLoadingSymbolDetail(false);
        }
      }
    );
  };

  public closeMarketOrder = async ({ payload, onSuccess, onError, onFinally }: ICloseOrderActionParams) => {
    const { symbol, orderType, orderId, fillPolicy } = payload;
    const lotSize = Number(payload.lotSize);
    const expiration = payload.expiration
      ? getSecondsSinceEpoch(getUTCOffsetDateTimeAsDayjs({ input: payload.expiration }))
      : undefined;
    if (symbol && orderId && lotSize && isMarketOrder(orderType)) {
      openOrderService.setLoadingCloseOrder(true);
      await tradeAPIController.closeMarketOrder(
        {
          orderId,
          symbol,
          lotSize: Math.abs(lotSize),
          orderType,
          fillPolicy,
          expiration
        },
        {
          onSuccess: () => {
            const integration = companyService.getCompanyIntegration();
            const isCentroid = integration === EMainProductCodeType.CENTROID;

            if (isCentroid) {
              // For Centroid: need to await socket result to close order tracker
              // This will handle in MainViewController.metaTraderTopicReceiver
              equityChartViewController.setPendingCloseOrderTrackerId(orderId);
            } else {
              equityChartViewController.cancelTrackOrder(orderId);
            }
            onSuccess();

            // Only optimistically remove the order if it's a full close.
            // For partial close, the order still exists with reduced volume — let the socket refresh update it.
            const existingOrder = orderListingService.context.openPositionsTableDisplay.find(
              (o) => o.orderId === orderId
            );
            const isFullClose = !existingOrder || Math.abs(lotSize) >= Math.abs(existingOrder.lotSize);
            if (isFullClose) {
              orderListingViewController.optimisticallyDeleteOpenOrdersFromTable([orderId]);
            }
          },
          onError: (error) => {
            const { code, message } = error as IApiError;
            const errorMessage = mapResponseCodeToMessage(code, message);
            onError(errorMessage);
          },
          onFinally: () => {
            openOrderService.setLoadingCloseOrder(false);
            onFinally?.();
          }
        }
      );
    }
  };
}

export const openOrderViewController = new OpenOrderViewController();
